import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createPortal } from 'react-dom';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { addNotePayable, getVendors, addVendor, clearError, clearSuccess, getProjects, addProject, getCustomers, addCustomer, getCostCenters, addCostCenter, getTaxes, addTax } from '../../../../redux/Slices/authSlice';

//add note payable component
const AddNotePayable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const {
    vendors, vendorsPagination,
    projects, projectsPagination,
    customers, customersPagination, // For project modal
    taxes, taxesPagination, // For project modal
    costCenters, costCentersPagination, // For project modal
    isLoading, error, success
  } = useSelector((s) => s.auth);

  const [form, setForm] = useState({
    vendor_id: '',
    project_id: '',
    amount: '',
    issue_date: '',
    due_date: '',
    note_number: '',
    description: '',
  });

  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [vendorCurrentPage, setVendorCurrentPage] = useState(1);
  const vendorSearchInputRef = useRef(null);
  const vendorSearchTimeoutRef = useRef(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const isSubOperationRef = useRef(false);

  // Projects dropdown state
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectCurrentPage, setProjectCurrentPage] = useState(1);
  const projectSearchInputRef = useRef(null);
  const projectSearchTimeoutRef = useRef(null);

  // Project Modal Dropdowns State
  const [projModalCustomerDropdownOpen, setProjModalCustomerDropdownOpen] = useState(false);
  const [projModalCustomerSearchTerm, setProjModalCustomerSearchTerm] = useState('');
  const [projModalCustomerCurrentPage, setProjModalCustomerCurrentPage] = useState(1);
  const projModalCustomerSearchInputRef = useRef(null);
  const projModalCustomerSearchTimeoutRef = useRef(null);

  const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
  const [taxSearchTerm, setTaxSearchTerm] = useState('');
  const [taxCurrentPage, setTaxCurrentPage] = useState(1);
  const taxSearchInputRef = useRef(null);
  const taxSearchTimeoutRef = useRef(null);

  const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
  const [costCenterSearchTerm, setCostCenterSearchTerm] = useState('');
  const [costCenterCurrentPage, setCostCenterCurrentPage] = useState(1);
  const costCenterSearchInputRef = useRef(null);
  const costCenterSearchTimeoutRef = useRef(null);

  // Modals state
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showAddTaxModal, setShowAddTaxModal] = useState(false);
  const [showAddCostCenterModal, setShowAddCostCenterModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false); // Needed for adding customer inside project modal

  const [vendorForm, setVendorForm] = useState({
    name_en: '',
    name_ar: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    tax_number: ''
  });
  const [isSubmittingVendor, setIsSubmittingVendor] = useState(false);

  const [projectForm, setProjectForm] = useState({
    code: '',
    name_ar: '',
    name_en: '',
    customer_id: null,
    start_date: '',
    end_date: '',
    status: 'planned',
    contract_value: '',
    tax_id: null,
    cost_center_id: null,
    notes: '',
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);
  const projectNotesTextareaRef = useRef(null);

  const [taxForm, setTaxForm] = useState({
    name_ar: '',
    name_en: '',
    rate: '',
    is_active: true,
  });

  const [costCenterForm, setCostCenterForm] = useState({
    name_ar: '',
    name_en: '',
    code: '',
  });

  const [customerForm, setCustomerForm] = useState({
    name_en: '',
    name_ar: '',
    code: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    tax_number: '',
    is_active: true,
    notes: ''
  });
  const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);

  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (vendorDropdownOpen) {
      dispatch(getVendors({ page: vendorCurrentPage, per_page: 10, search: vendorSearchTerm || undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorCurrentPage, vendorDropdownOpen, vendorSearchTerm]);

  // handle success
  useEffect(() => {
    if (success) {
      const message = typeof success === 'string' ? success : (success?.message || t('common.success'));
      toast.success(message, {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000
      });

      if (!isSubOperationRef.current) {
        const timer = setTimeout(() => {
          navigate('/notes-payable');
          dispatch(clearSuccess());
        }, 1500);
        return () => clearTimeout(timer);
      } else {
        dispatch(clearSuccess());
        isSubOperationRef.current = false;
      }
    }
  }, [success, dispatch, navigate, isRTL, t]);

  // handle error 
  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      const errorMessage = typeof error === 'string' ? error : (error?.message || t('common.error'));
      if (!last.message || last.message !== errorMessage || now - last.time > 2000) {
        toast.error(errorMessage, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 4000
        });
        lastErrorRef.current = { message: errorMessage, time: now };
      }
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch, isRTL, t]);

  // Search vendors with debounce
  useEffect(() => {
    if (vendorSearchTerm !== undefined && vendorDropdownOpen) {
      if (vendorSearchTimeoutRef.current) clearTimeout(vendorSearchTimeoutRef.current);
      vendorSearchTimeoutRef.current = setTimeout(() => {
        setVendorCurrentPage(1);
        dispatch(getVendors({ page: 1, per_page: 10, search: vendorSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (vendorSearchTimeoutRef.current) clearTimeout(vendorSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorSearchTerm, vendorDropdownOpen]);

  // Projects fetching
  useEffect(() => {
    if (projectDropdownOpen) {
      dispatch(getProjects({ page: projectCurrentPage, per_page: 10, search: projectSearchTerm || undefined }));
    }
  }, [dispatch, projectCurrentPage, projectDropdownOpen]);

  useEffect(() => {
    if (projectSearchTerm !== undefined && projectDropdownOpen) {
      if (projectSearchTimeoutRef.current) clearTimeout(projectSearchTimeoutRef.current);
      projectSearchTimeoutRef.current = setTimeout(() => {
        setProjectCurrentPage(1);
        dispatch(getProjects({ page: 1, per_page: 10, search: projectSearchTerm || undefined }));
      }, 300);
    }
  }, [projectSearchTerm, projectDropdownOpen, dispatch]);

  // Customer fetching (for Project Modal)
  useEffect(() => {
    if (projModalCustomerDropdownOpen) {
      dispatch(getCustomers({ page: projModalCustomerCurrentPage, per_page: 10, search: projModalCustomerSearchTerm || undefined }));
    }
  }, [dispatch, projModalCustomerCurrentPage, projModalCustomerDropdownOpen]);

  useEffect(() => {
    if (projModalCustomerSearchTerm !== undefined && projModalCustomerDropdownOpen) {
      if (projModalCustomerSearchTimeoutRef.current) clearTimeout(projModalCustomerSearchTimeoutRef.current);
      projModalCustomerSearchTimeoutRef.current = setTimeout(() => {
        setProjModalCustomerCurrentPage(1);
        dispatch(getCustomers({ page: 1, per_page: 10, search: projModalCustomerSearchTerm || undefined }));
      }, 300);
    }
  }, [projModalCustomerSearchTerm, projModalCustomerDropdownOpen, dispatch]);

  // Taxes fetching
  useEffect(() => {
    if (taxDropdownOpen) {
      dispatch(getTaxes({ page: taxCurrentPage, per_page: 10, search: taxSearchTerm || undefined }));
    }
  }, [dispatch, taxCurrentPage, taxDropdownOpen]);

  useEffect(() => {
    if (taxSearchTerm !== undefined && taxDropdownOpen) {
      if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
      taxSearchTimeoutRef.current = setTimeout(() => {
        setTaxCurrentPage(1);
        dispatch(getTaxes({ page: 1, per_page: 10, search: taxSearchTerm || undefined }));
      }, 300);
    }
  }, [taxSearchTerm, taxDropdownOpen, dispatch]);

  // Cost Centers fetching
  useEffect(() => {
    if (costCenterDropdownOpen) {
      dispatch(getCostCenters({ page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined }));
    }
  }, [dispatch, costCenterCurrentPage, costCenterDropdownOpen]);

  useEffect(() => {
    if (costCenterSearchTerm !== undefined && costCenterDropdownOpen) {
      if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
      costCenterSearchTimeoutRef.current = setTimeout(() => {
        setCostCenterCurrentPage(1);
        dispatch(getCostCenters({ page: 1, per_page: 10, search: costCenterSearchTerm || undefined }));
      }, 300);
    }
  }, [costCenterSearchTerm, costCenterDropdownOpen, dispatch]);

  useEffect(() => {
    if (projectDropdownOpen && projectSearchInputRef.current) {
      projectSearchInputRef.current.focus();
    }
  }, [projectDropdownOpen]);

  // Use vendors directly from API (search is handled server-side)
  const displayVendors = Array.isArray(vendors) ? vendors : [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleVendorFormChange = (e) => {
    const { name, value } = e.target;
    setVendorForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddVendor = async (e) => {
    e.preventDefault();
    if (!vendorForm.name_ar || !vendorForm.name_en) {
      toast.error(t('vendors.add.requiredFields'), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingVendor(true);
      isSubOperationRef.current = true;
      const result = await dispatch(addVendor(vendorForm)).unwrap();
      if (result?.data?.id) {
        // Set the newly added vendor in form
        setForm((prev) => ({ ...prev, vendor_id: result.data.id }));
        // Refresh vendors list
        await dispatch(getVendors({ page: vendorCurrentPage, per_page: 10, search: vendorSearchTerm || undefined }));
        setShowAddVendorModal(false);
        setVendorForm({
          name_en: '',
          name_ar: '',
          contact_person: '',
          email: '',
          phone: '',
          address: '',
          tax_number: ''
        });
        // Don't show toast here - let the API success message handle it via useEffect
      }
    } catch (error) {
      isSubOperationRef.current = false; // Reset flag on error
      // Error handled by effect
    } finally {
      setIsSubmittingVendor(false);
    }
  };

  /* Project Form Handlers */
  const handleProjectFormChange = (e) => {
    const { name, value } = e.target;
    const isTextarea = e.target.tagName === 'TEXTAREA';
    const cursorPosition = isTextarea ? e.target.selectionStart : null;

    setProjectForm(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'notes' && projectNotesTextareaRef.current && isTextarea) {
      setTimeout(() => {
        const textarea = projectNotesTextareaRef.current;
        if (textarea) {
          textarea.focus();
          if (cursorPosition !== null) {
            textarea.setSelectionRange(cursorPosition, cursorPosition);
          }
        }
      }, 0);
    }
  };

  const handleTaxFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTaxForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCostCenterFormChange = (e) => {
    const { name, value } = e.target;
    setCostCenterForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Helper to render label
  const renderDropdownItemLabel = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || '';
    const name = isRTL
      ? (item[nameArField] || item[nameEnField] || '')
      : (item[nameEnField] || item[nameArField] || '');
    return `${code ? `${code} - ` : ''}${name}`;
  };

  const renderProjectDisplay = (projectId) => {
    if (!projectId) return t('notesReceivable.form.selectProject', { defaultValue: 'اختر المشروع' });
    const selected = (projects || []).find((p) => String(p.id) === String(projectId));
    return selected ? renderDropdownItemLabel(selected, 'code', 'name_ar', 'name_en') : t('notesReceivable.form.selectProject', { defaultValue: 'اختر المشروع' });
  };


  const handleAddProject = async (e) => {
    e.preventDefault();
    if (!projectForm.name_ar) {
      toast.error(t('quotations.add.modals.addProject.requiredFields', { defaultValue: 'برجاء إدخال الاسم بالعربية' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingProject(true);
      const payload = {
        ...projectForm,
        contract_value: projectForm.contract_value ? Number(projectForm.contract_value) : undefined,
      };
      isSubOperationRef.current = true;
      const result = await dispatch(addProject(payload)).unwrap();
      if (result?.data?.id) {
        setForm(prev => ({ ...prev, project_id: result.data.id }));
        await dispatch(getProjects({ page: 1, per_page: 10 }));
        setShowAddProjectModal(false);
        setProjectForm({
          code: '',
          name_ar: '',
          name_en: '',
          customer_id: null,
          start_date: '',
          end_date: '',
          status: 'planned',
          contract_value: '',
          tax_id: null,
          cost_center_id: null,
          notes: '',
        });
      }
    } catch (error) {
      // Error handled by effect
    } finally {
      setIsSubmittingProject(false);
    }
  };


  // Add Customer Logic (for Project Modal)
  const handleAddCustomer = async (e) => {
    e.preventDefault();
    if (!customerForm.name_en) {
      toast.error(t('customers.add.nameEnRequired', { defaultValue: 'الاسم بالإنجليزية مطلوب' }), { rtl: isRTL });
      return;
    }
    if (!customerForm.name_ar) {
      toast.error(t('customers.add.nameArRequired', { defaultValue: 'الاسم بالعربية مطلوب' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingCustomer(true);
      isSubOperationRef.current = true;
      const result = await dispatch(addCustomer({ ...customerForm, is_active: customerForm.is_active ? 1 : 0 })).unwrap();
      if (result) {
        setProjectForm(prev => ({ ...prev, customer_id: result.data.id }));
        // Refresh customer list
        await dispatch(getCustomers({ page: 1, per_page: 10 }));
        setShowAddCustomerModal(false);
        setCustomerForm({
          name_en: '',
          name_ar: '',
          code: '',
          contact_person: '',
          email: '',
          phone: '',
          address: '',
          tax_number: '',
          is_active: true,
          notes: ''
        });
      }
    } catch (error) {
      // Error handled by effect
    } finally {
      setIsSubmittingCustomer(false);
    }
  };

  const handleCustomerFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCustomerForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddTax = async (e) => {
    e.preventDefault();
    if (!taxForm.name_ar || !taxForm.name_en || !taxForm.rate) {
      toast.error(t('quotations.add.modals.addTax.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    try {
      const payload = { ...taxForm, rate: Number(taxForm.rate) };
      isSubOperationRef.current = true;
      const result = await dispatch(addTax(payload)).unwrap();
      if (result?.data?.id) {
        setProjectForm(prev => ({ ...prev, tax_id: result.data.id }));
        await dispatch(getTaxes({ page: 1, per_page: 10 }));
        setShowAddTaxModal(false);
        setTaxForm({ name_ar: '', name_en: '', rate: '', is_active: true });
      }
    } catch (error) {
      // Error handled by effect
    }
  };

  const handleAddCostCenter = async (e) => {
    e.preventDefault();
    if (!costCenterForm.name_ar || !costCenterForm.name_en) {
      toast.error(t('quotations.add.modals.addCostCenter.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    try {
      isSubOperationRef.current = true;
      const result = await dispatch(addCostCenter(costCenterForm)).unwrap();
      if (result?.data?.id) {
        setProjectForm(prev => ({ ...prev, cost_center_id: result.data.id }));
        await dispatch(getCostCenters({ page: 1, per_page: 10 }));
        setShowAddCostCenterModal(false);
        setCostCenterForm({ name_ar: '', name_en: '', code: '' });
      }
    } catch (error) {
      // Error handled by effect
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside any dropdown container
      if (event.target.closest('[data-dropdown-container]')) return;

      setVendorDropdownOpen(false);
      setProjectDropdownOpen(false);

      // Close modal dropdowns
      setTaxDropdownOpen(false);
      setCostCenterDropdownOpen(false);
      setProjModalCustomerDropdownOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!form.vendor_id) {
      toast.error(t('notesPayable.form.requiredFields'), { rtl: isRTL });
      return;
    }
    if (!form.amount || form.amount === '' || form.amount === '0') {
      toast.error(t('notesPayable.form.amountRequired', { defaultValue: 'القيمة مطلوبة' }), { rtl: isRTL });
      return;
    }
    setIsSubmitting(true);
    const payload = {
      vendor_id: Number(form.vendor_id),
      amount: Number(form.amount),
    };

    if (form.project_id) {
      payload.project_id = Number(form.project_id);
    }

    // Only include optional fields if they have values
    if (form.issue_date) {
      payload.issue_date = form.issue_date;
    }
    if (form.due_date) {
      payload.due_date = form.due_date;
    }
    if (form.note_number) {
      payload.note_number = form.note_number;
    }
    if (form.description) {
      payload.description = form.description;
    }

    try {
      await dispatch(addNotePayable(payload)).unwrap();
    } catch (err) {
      // error handled by error useEffect
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderVendorDisplay = (vendorId) => {
    if (!vendorId) return t('notesPayable.form.selectVendor');
    const selected = (vendors || []).find((v) => String(v.id) === String(vendorId));
    if (!selected) return t('notesPayable.form.selectVendor');
    return `${selected.code ? `${selected.code} - ` : ''}${isRTL ? (selected.name_ar || selected.name || '') : (selected.name_en || selected.name_ar || selected.name || '')}`;
  };

  useEffect(() => {
    if (vendorDropdownOpen && vendorSearchInputRef.current) {
      vendorSearchInputRef.current.focus();
    }
  }, [vendorDropdownOpen]);

  const vendorPagination = vendorsPagination || { current_page: 1, last_page: 1 };

  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir }}>
      <h1 style={{ marginTop: 0, marginBottom: 16, fontSize: '20px', textAlign }}>{t('notesPayable.form.title')}</h1>
      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: '#1b3350',
          border: '1px solid #333',
          borderRadius: 12,
          padding: 24,
          display: 'grid',
          gap: 16,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <label style={{ display: 'block', fontSize: 14, textAlign, flex: 1 }}>
                {t('notesPayable.form.vendor')} <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowAddVendorModal(true);
                }}
                style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                title={t('vendors.add.title')}
              >
                <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
              </button>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div
                data-dropdown-container
                data-vendor-dropdown
                style={{ flex: 1, position: 'relative' }}
              >
                <div
                  data-dropdown-toggle
                  onClick={(e) => {
                    e.stopPropagation();
                    setProjectDropdownOpen(false);
                    setVendorDropdownOpen((p) => !p);
                  }}
                  style={{
                    width: '100%',
                    padding: 12,
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 14,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    direction: dir,
                    textAlign
                  }}
                >
                  <span>{renderVendorDisplay(form.vendor_id)}</span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
              </div>
            </div>
            {vendorDropdownOpen && (
              <div
                data-vendor-dropdown
                style={{
                  position: 'absolute',
                  zIndex: 10,
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: 8,
                  marginTop: 4,
                  maxHeight: 400,
                  overflowY: 'auto',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ padding: 8 }}>
                  <input
                    ref={vendorSearchInputRef}
                    type="text"
                    placeholder={t('notesPayable.form.searchVendor')}
                    value={vendorSearchTerm}
                    onChange={(e) => setVendorSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 8,
                      backgroundColor: '#14263b',
                      border: '1px solid #444',
                      borderRadius: 6,
                      color: 'white',
                      fontSize: 14,
                      direction: dir,
                      textAlign
                    }}
                    dir={dir}
                  />
                </div>
                {vendorPagination && vendorPagination.last_page >= 1 && (
                  <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ color: '#ccc', fontSize: 11, direction: dir, textAlign }}>{t('notesPayable.form.page')} {vendorPagination.current_page} {t('notesPayable.form.of')} {vendorPagination.last_page}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (vendorPagination.current_page > 1) {
                            setVendorCurrentPage(1);
                          }
                        }}
                        disabled={vendorPagination.current_page === 1}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: vendorPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                          color: vendorPagination.current_page === 1 ? '#666' : 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: vendorPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                          fontSize: 10,
                          fontWeight: 'bold',
                          minWidth: '28px',
                        }}
                        title={t('notesPayable.form.firstPage')}
                      >
                        ««
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (vendorPagination.current_page > 1) {
                            setVendorCurrentPage(vendorPagination.current_page - 1);
                          }
                        }}
                        disabled={vendorPagination.current_page === 1}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: vendorPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                          color: vendorPagination.current_page === 1 ? '#666' : 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: vendorPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                          fontSize: 10,
                          fontWeight: 'bold',
                          minWidth: '28px',
                        }}
                        title={t('notesPayable.form.previous')}
                      >
                        ‹
                      </button>
                      {(() => {
                        const pages = [];
                        const current = vendorPagination.current_page;
                        const last = vendorPagination.last_page;
                        const showPages = 5;
                        let start = Math.max(1, current - Math.floor(showPages / 2));
                        let end = Math.min(last, start + showPages - 1);
                        if (end - start < showPages - 1) {
                          start = Math.max(1, end - showPages + 1);
                        }
                        for (let i = start; i <= end; i++) {
                          pages.push(i);
                        }
                        return pages.map((page) => (
                          <button
                            key={page}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setVendorCurrentPage(page);
                            }}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: page === current ? '#AC2000' : '#1a1f2e',
                              color: 'white',
                              border: page === current ? '1px solid #AC2000' : '1px solid #444',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: 10,
                              fontWeight: page === current ? 'bold' : 'normal',
                              minWidth: '28px',
                            }}
                          >
                            {page}
                          </button>
                        ));
                      })()}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (vendorPagination.current_page < vendorPagination.last_page) {
                            setVendorCurrentPage(vendorPagination.current_page + 1);
                          }
                        }}
                        disabled={vendorPagination.current_page === vendorPagination.last_page}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: vendorPagination.current_page === vendorPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                          color: vendorPagination.current_page === vendorPagination.last_page ? '#666' : 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: vendorPagination.current_page === vendorPagination.last_page ? 'not-allowed' : 'pointer',
                          fontSize: 10,
                          fontWeight: 'bold',
                          minWidth: '28px',
                        }}
                        title={t('notesPayable.form.next')}
                      >
                        ›
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (vendorPagination.current_page < vendorPagination.last_page) {
                            setVendorCurrentPage(vendorPagination.last_page);
                          }
                        }}
                        disabled={vendorPagination.current_page === vendorPagination.last_page}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: vendorPagination.current_page === vendorPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                          color: vendorPagination.current_page === vendorPagination.last_page ? '#666' : 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: vendorPagination.current_page === vendorPagination.last_page ? 'not-allowed' : 'pointer',
                          fontSize: 10,
                          fontWeight: 'bold',
                          minWidth: '28px',
                        }}
                        title={t('notesPayable.form.lastPage')}
                      >
                        »»
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  {displayVendors.map((v) => (
                    <div
                      key={v.id}
                      onClick={() => {
                        setForm((prev) => ({ ...prev, vendor_id: v.id }));
                        setVendorDropdownOpen(false);
                        setVendorSearchTerm('');
                      }}
                      style={{
                        padding: 10,
                        cursor: 'pointer',
                        borderTop: '1px solid #333',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span>{`${v.code ? `${v.code} - ` : ''}${isRTL ? (v.name_ar || v.name || '') : (v.name_en || v.name_ar || v.name || '')}`}</span>
                    </div>
                  ))}
                  {/* Add extra space at the bottom to ensure last item is visible, even with scrollbars */}
                  <div style={{ height: 40 }} />
                  {displayVendors.length === 0 && (
                    <div style={{ padding: 12, textAlign: 'center', color: '#bbb' }}>{t('notesPayable.form.noResults')}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Project Dropdown */}
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <label style={{ display: 'block', fontSize: 14, textAlign, flex: 1 }}>
                {t('notesReceivable333.form.project', { defaultValue: 'المشروع' })}
              </label>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div
                data-dropdown-container
                style={{ flex: 1, position: 'relative' }}
              >
                <div
                  data-dropdown-toggle
                  onClick={(e) => {
                    e.stopPropagation();
                    setVendorDropdownOpen(false);
                    setProjectDropdownOpen((p) => !p);
                  }}
                  style={{
                    width: '100%',
                    padding: 12,
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 14,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    direction: dir,
                    textAlign
                  }}
                >
                  <span>{renderProjectDisplay(form.project_id)}</span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
                {projectDropdownOpen && (
                  <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4, maxHeight: 400, overflowY: 'auto' }}>
                    <div style={{ padding: 8 }}>
                      <input
                        ref={projectSearchInputRef}
                        type="text"
                        placeholder={t('quotations.add.search', { defaultValue: 'ابحث عن مشروع' })}
                        value={projectSearchTerm}
                        onChange={(e) => setProjectSearchTerm(e.target.value)}
                        style={{
                          width: '100%',
                          padding: 8,
                          backgroundColor: '#14263b',
                          border: '1px solid #444',
                          borderRadius: 6,
                          color: 'white',
                          fontSize: 14,
                          direction: dir,
                          textAlign
                        }}
                        dir={dir}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {projectsPagination && projectsPagination.last_page >= 1 && (
                      <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ color: '#ccc', fontSize: 11, direction: dir, textAlign }}>{t('notesPayable.form.page')} {projectsPagination.current_page} {t('notesPayable.form.of')} {projectsPagination.last_page}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (projectsPagination.current_page > 1) {
                                setProjectCurrentPage(1);
                              }
                            }}
                            disabled={projectsPagination.current_page === 1}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: projectsPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                              color: projectsPagination.current_page === 1 ? '#666' : 'white',
                              border: '1px solid #444',
                              borderRadius: '4px',
                              cursor: projectsPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                              fontSize: 10,
                              fontWeight: 'bold',
                              minWidth: '28px',
                            }}
                            title={t('notesPayable.form.firstPage', { defaultValue: 'First' })}
                          >
                            ««
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (projectsPagination.current_page > 1) {
                                setProjectCurrentPage(projectsPagination.current_page - 1);
                              }
                            }}
                            disabled={projectsPagination.current_page === 1}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: projectsPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                              color: projectsPagination.current_page === 1 ? '#666' : 'white',
                              border: '1px solid #444',
                              borderRadius: '4px',
                              cursor: projectsPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                              fontSize: 10,
                              fontWeight: 'bold',
                              minWidth: '28px',
                            }}
                            title={t('notesPayable.form.previous', { defaultValue: 'Previous' })}
                          >
                            ‹
                          </button>
                          {(() => {
                            const pages = [];
                            const current = projectsPagination.current_page;
                            const last = projectsPagination.last_page;
                            const showPages = 5;
                            let start = Math.max(1, current - Math.floor(showPages / 2));
                            let end = Math.min(last, start + showPages - 1);
                            if (end - start < showPages - 1) {
                              start = Math.max(1, end - showPages + 1);
                            }
                            for (let i = start; i <= end; i++) {
                              pages.push(i);
                            }
                            return pages.map((page) => (
                              <button
                                key={page}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setProjectCurrentPage(page);
                                }}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: page === current ? '#AC2000' : '#1a1f2e',
                                  color: 'white',
                                  border: page === current ? '1px solid #AC2000' : '1px solid #444',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: 10,
                                  fontWeight: page === current ? 'bold' : 'normal',
                                  minWidth: '28px',
                                }}
                              >
                                {page}
                              </button>
                            ));
                          })()}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (projectsPagination.current_page < projectsPagination.last_page) {
                                setProjectCurrentPage(projectsPagination.current_page + 1);
                              }
                            }}
                            disabled={projectsPagination.current_page === projectsPagination.last_page}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: projectsPagination.current_page === projectsPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                              color: projectsPagination.current_page === projectsPagination.last_page ? '#666' : 'white',
                              border: '1px solid #444',
                              borderRadius: '4px',
                              cursor: projectsPagination.current_page === projectsPagination.last_page ? 'not-allowed' : 'pointer',
                              fontSize: 10,
                              fontWeight: 'bold',
                              minWidth: '28px',
                            }}
                            title={t('notesPayable.form.next', { defaultValue: 'Next' })}
                          >
                            ›
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (projectsPagination.current_page < projectsPagination.last_page) {
                                setProjectCurrentPage(projectsPagination.last_page);
                              }
                            }}
                            disabled={projectsPagination.current_page === projectsPagination.last_page}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: projectsPagination.current_page === projectsPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                              color: projectsPagination.current_page === projectsPagination.last_page ? '#666' : 'white',
                              border: '1px solid #444',
                              borderRadius: '4px',
                              cursor: projectsPagination.current_page === projectsPagination.last_page ? 'not-allowed' : 'pointer',
                              fontSize: 10,
                              fontWeight: 'bold',
                              minWidth: '28px',
                            }}
                            title={t('notesPayable.form.lastPage', { defaultValue: 'Last' })}
                          >
                            »»
                          </button>
                        </div>
                      </div>
                    )}
                    <div>
                      {(projects || []).map((p) => (
                        <div
                          key={p.id}
                          onClick={() => {
                            setForm((prev) => ({ ...prev, project_id: p.id }));
                            setProjectDropdownOpen(false);
                            setProjectSearchTerm('');
                          }}
                          style={{
                            padding: 10,
                            cursor: 'pointer',
                            borderTop: '1px solid #333',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span>{renderDropdownItemLabel(p, 'code', 'name_ar', 'name_en')}</span>
                        </div>
                      ))}
                      {(projects || []).length === 0 && (
                        <div style={{ padding: 12, textAlign: 'center', color: '#bbb' }}>{t('quotations.add.noResults', { defaultValue: 'لا توجد نتائج' })}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowAddProjectModal(true);
                }}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '44px',
                  height: '44px'
                }}
                title={t('quotations.add.modals.addProject.title', { defaultValue: 'إضافة مشروع جديد' })}
              >
                <FaPlus />
              </button>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign }}>
              {t('notesPayable.form.amount', { defaultValue: 'القيمة' })} <span style={{ color: '#AC2000' }}>*</span>
            </label>
            <input
              type="number"

              name="amount"
              value={form.amount}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                direction: dir,
                textAlign
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign }}>
              {t('notesPayable.form.issueDate')}
            </label>
            <input
              type="date"
              name="issue_date"
              value={form.issue_date}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                direction: dir,
                textAlign
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign }}>
              {t('notesPayable.form.dueDate')}
            </label>
            <input
              type="date"
              name="due_date"
              value={form.due_date}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                direction: dir,
                textAlign
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign }}>
              {t('notesPayable.form.noteNumber')}
            </label>
            <input
              type="text"
              name="note_number"
              value={form.note_number}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                direction: dir,
                textAlign
              }}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign }}>{t('notesPayable.form.description')}</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              style={{
                width: '100%',
                padding: 12,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                resize: 'vertical',
                direction: dir,
                textAlign
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <button
            type="button"
            onClick={() => navigate('/notes-payable')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
              direction: dir,
              textAlign
            }}
          >
            {t('notesPayable.form.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
              direction: dir,
              textAlign
            }}
          >
            {t('notesPayable.form.save')}
          </button>
        </div>
      </form>

      {/* Modal لإضافة مورد جديد */}
      {showAddVendorModal && createPortal(
        <div
          data-modal-overlay
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddVendorModal(false)}
        >
          <div
            data-modal-content
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign }}>{t('vendors.add.title')}</h2>
              <button
                onClick={() => {
                  setShowAddVendorModal(false);
                  setVendorForm({
                    name_en: '',
                    name_ar: '',
                    contact_person: '',
                    email: '',
                    phone: '',
                    address: '',
                    tax_number: ''
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddVendor}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                {isRTL ? (
                  <>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                        {t('vendors.add.nameAr')} <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name_ar"
                        value={vendorForm.name_ar}
                        onChange={handleVendorFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: 12,
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: 8,
                          color: 'white',
                          fontSize: 14,
                          boxSizing: 'border-box',
                          direction: 'rtl',
                          textAlign: 'right'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: "white" }}>
                        {t('vendors.add.nameEn')} <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name_en"
                        value={vendorForm.name_en}
                        onChange={handleVendorFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: 12,
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: 8,
                          color: 'white',
                          fontSize: 14,
                          boxSizing: 'border-box',
                          direction: 'ltr',
                          textAlign: 'left'
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                        {t('vendors.add.nameEn')} <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name_en"
                        value={vendorForm.name_en}
                        onChange={handleVendorFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: 12,
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: 8,
                          color: 'white',
                          fontSize: 14,
                          boxSizing: 'border-box',
                          direction: 'ltr',
                          textAlign: 'left'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                        {t('vendors.add.nameAr')} <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name_ar"
                        value={vendorForm.name_ar}
                        onChange={handleVendorFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: 12,
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: 8,
                          color: 'white',
                          fontSize: 14,
                          boxSizing: 'border-box',
                          direction: 'rtl',
                          textAlign: 'right'
                        }}
                      />
                    </div>
                  </>
                )}
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {t('vendors.add.contactPerson')}
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={vendorForm.contact_person}
                    onChange={handleVendorFormChange}
                    style={{
                      width: '100%',
                      padding: 12,
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {t('vendors.add.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={vendorForm.email}
                    onChange={handleVendorFormChange}
                    style={{
                      width: '100%',
                      padding: 12,
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {t('vendors.add.phone')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={vendorForm.phone}
                    onChange={handleVendorFormChange}
                    style={{
                      width: '100%',
                      padding: 12,
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {t('vendors.add.taxNumber')}
                  </label>
                  <input
                    type="text"
                    name="tax_number"
                    value={vendorForm.tax_number}
                    onChange={handleVendorFormChange}
                    style={{
                      width: '100%',
                      padding: 12,
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ gridColumn: '1 / span 2' }}>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {t('vendors.add.address')}
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={vendorForm.address}
                    onChange={handleVendorFormChange}
                    style={{
                      width: '100%',
                      padding: 12,
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  type="submit"
                  disabled={isSubmittingVendor}
                  style={{
                    backgroundColor: isSubmittingVendor ? '#666' : '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: isSubmittingVendor ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    opacity: isSubmittingVendor ? 0.6 : 1
                  }}
                >
                  {isSubmittingVendor ? t('vendors.add.saving') : t('vendors.add.save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddVendorModal(false);
                    setVendorForm({
                      name_en: '',
                      name_ar: '',
                      contact_person: '',
                      email: '',
                      phone: '',
                      address: '',
                      tax_number: ''
                    });
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  {t('vendors.add.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Project Modal */}
      {showAddProjectModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddProjectModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.title', { defaultValue: 'إضافة مشروع جديد' })}</h2>
              <button
                onClick={() => setShowAddProjectModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddProject}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addProject.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={projectForm.name_ar}
                    onChange={handleProjectFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addProject.nameEn', { defaultValue: 'الاسم بالإنجليزية' })}
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={projectForm.name_en}
                    onChange={handleProjectFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addProject.code', { defaultValue: 'كود المشروع' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={projectForm.code}
                    onChange={handleProjectFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>

                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addProject.customer', { defaultValue: 'العميل' })}
                    </label>
                    {/* Add Customer Button inside Project Modal - Keeping logic simple/stubbed as requested */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddCustomerModal(true); }}
                      style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                    >
                      <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                    </button>
                  </div>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setTaxDropdownOpen(false);
                      setCostCenterDropdownOpen(false);
                      setProjModalCustomerDropdownOpen((p) => !p);
                    }}
                    style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                  >
                    <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {projectForm.customer_id ? (customers || []).find(c => String(c.id) === String(projectForm.customer_id)) ? renderDropdownItemLabel((customers || []).find(c => String(c.id) === String(projectForm.customer_id)), 'code', 'name_ar', 'name_en') : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' }) : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' })}
                    </span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  {projModalCustomerDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={projModalCustomerDropdownOpen}
                      onToggle={setProjModalCustomerDropdownOpen}
                      searchTerm={projModalCustomerSearchTerm}
                      onSearchChange={(val) => {
                        setProjModalCustomerSearchTerm(val);
                        if (projModalCustomerSearchTimeoutRef.current) clearTimeout(projModalCustomerSearchTimeoutRef.current);
                        projModalCustomerSearchTimeoutRef.current = setTimeout(() => {
                          setProjModalCustomerCurrentPage(1);
                          dispatch(getCustomers({ page: 1, per_page: 10, search: val || undefined }));
                        }, 300);
                      }}
                      searchInputRef={projModalCustomerSearchInputRef}
                      items={Array.isArray(customers) ? customers : []}
                      pagination={customersPagination}
                      currentPage={projModalCustomerCurrentPage}
                      onPageChange={(page) => {
                        setProjModalCustomerCurrentPage(page);
                        dispatch(getCustomers({ page, per_page: 10, search: projModalCustomerSearchTerm || undefined }));
                      }}
                      onSelect={(id) => {
                        setProjectForm((prev) => ({ ...prev, customer_id: id }));
                        setProjModalCustomerDropdownOpen(false);
                      }}
                      label={t('quotations.add.modals.addProject.customer', { defaultValue: 'عميل' })}
                    />
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.startDate', { defaultValue: 'تاريخ البداية' })}</label>
                  <input type="date" name="start_date" value={projectForm.start_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.endDate', { defaultValue: 'تاريخ النهاية' })}</label>
                  <input type="date" name="end_date" value={projectForm.end_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.status', { defaultValue: 'الحالة' })}</label>
                  <select name="status" value={projectForm.status} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    <option value="planned">{t('quotations.add.modals.addProject.statusOptions.planned', { defaultValue: 'مخطط' })}</option>
                    <option value="in_progress">{t('quotations.add.modals.addProject.statusOptions.inProgress', { defaultValue: 'قيد التنفيذ' })}</option>
                    <option value="paused">{t('quotations.add.modals.addProject.statusOptions.paused', { defaultValue: 'متوقف' })}</option>
                    <option value="completed">{t('quotations.add.modals.addProject.statusOptions.completed', { defaultValue: 'مكتمل' })}</option>
                    <option value="cancelled">{t('quotations.add.modals.addProject.statusOptions.cancelled', { defaultValue: 'ملغى' })}</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.contractValue', { defaultValue: 'قيمة العقد' })}</label>
                  <input type="number" name="contract_value" value={projectForm.contract_value} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>

                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}
                    </label>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddTaxModal(true); }}
                      style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                    >
                      <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                    </button>
                  </div>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setProjModalCustomerDropdownOpen(false);
                      setCostCenterDropdownOpen(false);
                      if (taxDropdownOpen) { setTaxSearchTerm(''); setTaxCurrentPage(1); }
                      setTaxDropdownOpen((p) => !p);
                    }}
                    style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                  >
                    <span>{projectForm.tax_id ? renderDropdownItemLabel((taxes || []).find(t => t.id === projectForm.tax_id) || {}, '', 'name_ar', 'name_en') : t('quotations.add.selectTax', { defaultValue: 'اختر الضريبة' })}</span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  {taxDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={taxDropdownOpen}
                      onToggle={setTaxDropdownOpen}
                      searchTerm={taxSearchTerm}
                      onSearchChange={setTaxSearchTerm}
                      searchInputRef={taxSearchInputRef}
                      items={taxes || []}
                      pagination={taxesPagination}
                      currentPage={taxCurrentPage}
                      onPageChange={setTaxCurrentPage}
                      onSelect={(id) => {
                        setProjectForm(prev => ({ ...prev, tax_id: id }));
                        setTaxDropdownOpen(false);
                      }}
                      label={t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}
                    />
                  )}
                </div>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}
                    </label>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddCostCenterModal(true); }}
                      style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                    >
                      <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                    </button>
                  </div>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setProjModalCustomerDropdownOpen(false);
                      setTaxDropdownOpen(false);
                      if (costCenterDropdownOpen) { setCostCenterSearchTerm(''); setCostCenterCurrentPage(1); }
                      setCostCenterDropdownOpen((p) => !p);
                    }}
                    style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                  >
                    <span>{projectForm.cost_center_id ? renderDropdownItemLabel((costCenters || []).find(c => c.id === projectForm.cost_center_id) || {}, 'code', 'name_ar', 'name_en') : t('common999.select', { defaultValue: 'اختر مركز التكلفة' })}</span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  {costCenterDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={costCenterDropdownOpen}
                      onToggle={setCostCenterDropdownOpen}
                      searchTerm={costCenterSearchTerm}
                      onSearchChange={setCostCenterSearchTerm}
                      searchInputRef={costCenterSearchInputRef}
                      items={costCenters || []}
                      pagination={costCentersPagination}
                      currentPage={costCenterCurrentPage}
                      onPageChange={setCostCenterCurrentPage}
                      onSelect={(id) => {
                        setProjectForm(prev => ({ ...prev, cost_center_id: id }));
                        setCostCenterDropdownOpen(false);
                      }}
                      label={t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}
                    />
                  )}
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('quotations.add.modals.addProject.notes', { defaultValue: 'ملاحظات' })}
                </label>
                <textarea
                  ref={projectNotesTextareaRef}
                  name="notes"
                  value={projectForm.notes}
                  onChange={handleProjectFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    resize: 'vertical',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddProjectModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingProject}
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isSubmittingProject ? 'not-allowed' : 'pointer', opacity: isSubmittingProject ? 0.7 : 1 }}
                >
                  {isSubmittingProject ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>
        , document.body)}

      {/* Add Tax Modal */}
      {showAddTaxModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddTaxModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                {t('quotations.add.modals.addTax.title', { defaultValue: 'إضافة ضريبة جديدة' })}
              </h2>
              <button
                onClick={() => setShowAddTaxModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddTax}>
              <div style={{ display: 'grid', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={taxForm.name_ar}
                    onChange={handleTaxFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'rtl',
                      textAlign: 'right'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={taxForm.name_en}
                    onChange={handleTaxFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.rate', { defaultValue: 'النسبة (%)' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="rate"
                    value={taxForm.rate}
                    onChange={handleTaxFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={taxForm.is_active}
                    onChange={(e) => setTaxForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer' }}>
                    {t('quotations.add.modals.addTax.isActive', { defaultValue: 'نشط' })}
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddTaxModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Cost Center Modal */}
      {showAddCostCenterModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddCostCenterModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                {t('quotations.add.modals.addCostCenter.title', { defaultValue: 'إضافة مركز تكلفة جديد' })}
              </h2>
              <button
                onClick={() => setShowAddCostCenterModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCostCenter}>
              <div style={{ display: 'grid', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCostCenter.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={costCenterForm.name_ar}
                    onChange={handleCostCenterFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'rtl',
                      textAlign: 'right'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCostCenter.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={costCenterForm.name_en}
                    onChange={handleCostCenterFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCostCenter.code', { defaultValue: 'الكود' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={costCenterForm.code}
                    onChange={handleCostCenterFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddCostCenterModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddCustomerModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                {t('customers.add.title', { defaultValue: 'إضافة عميل جديد' })}
              </h2>
              <button
                onClick={() => setShowAddCustomerModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCustomer}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={customerForm.name_ar}
                    onChange={handleCustomerFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'rtl',
                      textAlign: 'right'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={customerForm.name_en}
                    onChange={handleCustomerFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.code', { defaultValue: 'كود العميل' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={customerForm.code}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.contactPerson', { defaultValue: 'الشخص المسؤول' })}
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={customerForm.contact_person}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.email', { defaultValue: 'البريد الإلكتروني' })}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={customerForm.email}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.phone', { defaultValue: 'رقم الهاتف' })}
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={customerForm.phone}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.taxNumber', { defaultValue: 'الرقم الضريبي' })}
                  </label>
                  <input
                    type="text"
                    name="tax_number"
                    value={customerForm.tax_number}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={customerForm.is_active}
                    onChange={(e) => setCustomerForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer' }}>
                    {t('customers.add.status.isActive', { defaultValue: 'نشط' })}
                  </label>
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('customers.add.address', { defaultValue: 'العنوان' })}
                </label>
                <input
                  type="text"
                  name="address"
                  value={customerForm.address}
                  onChange={handleCustomerFormChange}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                />
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('customers.add.notes', { defaultValue: 'ملاحظات' })}
                </label>
                <textarea
                  name="notes"
                  value={customerForm.notes}
                  onChange={handleCustomerFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    resize: 'vertical',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddCustomerModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingCustomer}
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isSubmittingCustomer ? 'not-allowed' : 'pointer', opacity: isSubmittingCustomer ? 0.7 : 1 }}
                >
                  {isSubmittingCustomer ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

/* Dropdown Component */
const DropdownWithSearch = ({ isOpen, onToggle, searchTerm, onSearchChange, searchInputRef, items, pagination, currentPage, onPageChange, onSelect, displayValue, placeholder, label, renderItem }) => {
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';

  const renderDropdownItemLabel = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || '';
    const name = isRTL
      ? (item[nameArField] || item[nameEnField] || '')
      : (item[nameEnField] || item[nameArField] || '');
    return `${code ? `${code} - ` : ''}${name}`;
  };

  const internalInputRef = useRef(null);
  const inputRef = searchInputRef || internalInputRef;

  useEffect(() => {
    if (isOpen && inputRef?.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus({ preventScroll: true });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, inputRef]);

  if (!isOpen) return null;

  return (
    <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4, maxHeight: 400, overflowY: 'auto' }}>
      <div style={{ padding: 8 }}>
        <input
          ref={inputRef}
          type="text"
          placeholder={`${t('quotations.add.search', { defaultValue: 'ابحث عن' })} ${label}`}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              inputRef.current?.focus({ preventScroll: true });
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          style={{ width: '100%', padding: 8, backgroundColor: '#14263b', border: '1px solid #444', borderRadius: 6, color: 'white', fontSize: 14 }}
        />
      </div>
      {pagination && pagination.last_page >= 1 && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('common.pagination.page', { defaultValue: 'الصفحة' })} {pagination.current_page} {t('common.pagination.of', { defaultValue: 'من' })} {pagination.last_page}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div>
        {(Array.isArray(items) ? items : []).map((item) => (
          <div
            key={item.id}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSelect(item.id);
            }}
            style={{
              padding: '12px 15px',
              cursor: 'pointer',
              borderTop: '1px solid #333',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'white',
              fontSize: '14px',
              transition: 'background-color 0.2s',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#14263b'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span style={{ color: 'white' }}>
              {renderItem ? renderItem(item) : renderDropdownItemLabel(item, 'code', 'name_ar', 'name_en')}
            </span>
          </div>
        ))}
        {(!Array.isArray(items) || items.length === 0) && <div style={{ padding: 12, textAlign: 'center', color: '#bbb', direction: isRTL ? 'rtl' : 'ltr' }}>{t('quotations.add.noResults', { defaultValue: 'لا توجد نتائج' })}</div>}
      </div>
    </div>
  );
};

export default AddNotePayable;

